/**
 * Identificadores globales no secuenciales para entidades sincronizables.
 *
 * Se usa ULID (26 chars, Crockford base32): ordenable por tiempo de creacion,
 * generable offline sin coordinacion y sin revelar un consecutivo del negocio.
 * El generador es inyectable (reloj + fuente aleatoria) para pruebas deterministas.
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32, sin I L O U
const ENCODING_LEN = 32;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

export type Ulid = string;

export interface IdGeneratorOptions {
  /** Milisegundos desde epoch. Por defecto `Date.now`. */
  now?: () => number;
  /** Fuente aleatoria criptografica. Por defecto `crypto.getRandomValues`. */
  randomBytes?: (length: number) => Uint8Array;
}

function defaultRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function encodeTime(time: number, length: number): string {
  let remaining = time;
  let out = '';
  for (let i = length - 1; i >= 0; i -= 1) {
    const mod = remaining % ENCODING_LEN;
    out = ENCODING[mod] + out;
    remaining = (remaining - mod) / ENCODING_LEN;
  }
  return out;
}

function encodeRandom(randomBytes: (length: number) => Uint8Array, length: number): string {
  const bytes = randomBytes(length);
  let out = '';
  for (let i = 0; i < length; i += 1) {
    // Se descarta el sesgo despreciable de 256 % 32 === 0 (no hay sesgo: 256 es multiplo de 32).
    out += ENCODING[(bytes[i] ?? 0) % ENCODING_LEN];
  }
  return out;
}

export function createIdGenerator(options: IdGeneratorOptions = {}): () => Ulid {
  const now = options.now ?? (() => Date.now());
  const randomBytes = options.randomBytes ?? defaultRandomBytes;
  let lastTime = -1;
  let lastRandom = '';

  return function nextId(): Ulid {
    const time = now();
    if (time === lastTime) {
      // Monotonicidad dentro del mismo milisegundo: incrementa la parte aleatoria.
      lastRandom = incrementBase32(lastRandom);
    } else {
      lastTime = time;
      lastRandom = encodeRandom(randomBytes, RANDOM_LEN);
    }
    return encodeTime(time, TIME_LEN) + lastRandom;
  };
}

function incrementBase32(value: string): string {
  const chars = value.split('');
  for (let i = chars.length - 1; i >= 0; i -= 1) {
    const index = ENCODING.indexOf(chars[i] ?? '0');
    if (index < ENCODING_LEN - 1) {
      chars[i] = ENCODING[index + 1] as string;
      return chars.join('');
    }
    chars[i] = ENCODING[0] as string;
  }
  // Desbordamiento (imposible en la practica): reinicia.
  return ENCODING[0]!.repeat(value.length);
}

/** Generador por defecto del proceso. */
export const newId: () => Ulid = createIdGenerator();

const ULID_PATTERN = new RegExp(`^[${ENCODING}]{${TIME_LEN + RANDOM_LEN}}$`);

export function isUlid(value: unknown): value is Ulid {
  return typeof value === 'string' && ULID_PATTERN.test(value);
}

/** Instante de creacion embebido en el ULID (ms desde epoch). */
export function ulidTime(id: Ulid): number {
  let time = 0;
  for (let i = 0; i < TIME_LEN; i += 1) {
    time = time * ENCODING_LEN + ENCODING.indexOf(id[i] ?? '0');
  }
  return time;
}

/**
 * Clave idempotente de una operacion monetaria.
 *
 * Se deriva del ambito (institucion) + tipo + id de la operacion generado en el
 * dispositivo. Es estable ante reintentos: el mismo pago reenviado N veces produce
 * exactamente la misma clave, y la base central la tiene con UNIQUE por institucion.
 */
export function idempotencyKey(
  institutionId: string,
  operationType: string,
  operationId: Ulid,
): string {
  return `${institutionId}:${operationType}:${operationId}`;
}
